<template>
  <div class="pa-3">
    <!-- TOP BAR: Search + Add Button -->
    <v-row class="mb-4" align="center">
      <v-col cols="12" md="8">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Search by Reference or Display Name"
          outlined dense clearable
          class="rounded-lg small-text-field"
          hide-details
        />
      </v-col>
      <v-col cols="12" md="4" class="text-right">
        <v-btn
          @click="$router.push('/Addproperty')"
          small
          class="save-btn px-6"
          style="border-radius: 20px; font-weight: bold; font-size: 0.875rem;"
        >
          <v-icon left small>mdi-plus</v-icon> Add Property
        </v-btn>
      </v-col>
    </v-row>

    <!-- FULL WIDTH DATA TABLE -->
    <v-data-table
      :headers="headers"
      :items="properties"
      :search="search"
      :items-per-page="15"
      class="elevation-2 compact-table"
      style="border-radius: 12px; overflow: hidden;"
      :expanded.sync="expanded"
      show-expand
      dense
    >
      <!-- Status Chip -->
      <template v-slot:item.isVacant="{ item }">
        <v-chip :color="item.isVacant ? 'green' : 'red'" dark small>
          {{ item.isVacant ? 'Vacant' : 'Occupied' }}
        </v-chip>
      </template>

      <!-- Units Info -->
      <template v-slot:item.unitsInfo="{ item }">
        <span class="font-weight-medium black--text">{{ item.unitsInfo }}</span>
      </template>

      <!-- Action Buttons -->
      <template v-slot:item.actions="{ item }">
        <div class="d-flex align-center justify-center" style="gap: 4px;">
          <v-btn icon small @click="editProperty(item)" class="action-btn">
            <v-icon small color="grey darken-2">mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small @click="confirmDelete(item)" class="action-btn">
            <v-icon small color="red">mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>

      <!-- EXPANDED ROW CONTENT -->
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="pa-3" style="background: #f8f9fa;">
          <v-row dense>
            <v-col cols="12" md="4" class="py-1">
              <strong class="black--text">Reference:</strong>
              <span class="black--text">{{ item.property }}</span>
            </v-col>
            <v-col cols="12" md="4" class="py-1">
              <strong class="black--text">Display Name:</strong>
              <span class="black--text">{{ item.state }}</span>
            </v-col>
            <v-col cols="12" md="4" class="py-1">
              <strong class="black--text">Status:</strong>
              <span class="black--text">{{ item.isVacant ? 'Vacant' : 'Occupied' }}</span>
            </v-col>
          </v-row>
          <v-row dense class="mt-2">
            <v-col cols="12" md="4">
              <v-btn block class="golden-btn" small @click="goToAttachments(item)">
                <v-icon left small>mdi-folder-multiple</v-icon> Attachments
              </v-btn>
            </v-col>
            <v-col cols="12" md="4">
              <v-btn block class="golden-btn" small @click="goToNotes(item)">
                <v-icon left small>mdi-note-text</v-icon> Notes
              </v-btn>
            </v-col>
            <v-col cols="12" md="4">
              <v-btn block class="golden-btn" small @click="goToReports(item)">
                <v-icon left small>mdi-chart-bar</v-icon> Reports
              </v-btn>
            </v-col>
          </v-row>
        </td>
      </template>
    </v-data-table>

    <!-- DELETE CONFIRMATION -->
    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card class="pa-3">
        <v-card-title class="red--text text-subtitle-1 font-weight-bold">Delete Property?</v-card-title>
        <v-card-text class="black--text">
          Are you sure you want to delete <strong>{{ propertyToDelete?.property }}</strong>?<br>
          This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text small @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="red" dark small @click="deleteProperty">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getFirestore, collection, getDocs, deleteDoc, doc, addDoc } from "firebase/firestore";
import { firebaseApp } from "../firebaseConfig";

const db = getFirestore(firebaseApp);

// LOG EVENT FUNCTION
async function logEvent(action, target = "", type = "Property Management") {
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const event = {
    date: Math.floor(Date.now() / 1000),
    eventType: type,
    eventAction: action,
    user: currentUser.mobile || currentUser.email || "System",
    userName: `${currentUser.name || ""} ${currentUser.lastname || ""}`.trim(),
    targetEmail: target,
  };
  try {
    await addDoc(collection(db, "events"), event);
  } catch (e) {
    console.error("Event log failed:", e);
  }
}

export default {
  data() {
    return {
      search: "",
      expanded: [],
      deleteDialog: false,
      propertyToDelete: null,
      properties: [],
      headers: [
        { text: "Reference", value: "property", width: "120px" },
        { text: "Display Name", value: "state" },
        { text: "Status", value: "isVacant", align: "center", width: "110px" },
        { text: "Units", value: "unitsInfo", align: "center", width: "130px" },
        { text: "", value: "data-table-expand", width: "40px" },
        { text: "Actions", value: "actions", sortable: false, align: "center", width: "100px" },
      ],
    };
  },

  async mounted() {
    await this.fetchProperties();
  },

  methods: {
    async fetchProperties() {
      try {
        const snap = await getDocs(collection(db, "properties"));
        const props = await Promise.all(
          snap.docs.map(async (doc) => {
            const data = doc.data();
            const unitsSnap = await getDocs(collection(db, "properties", doc.id, "units"));
            const units = unitsSnap.docs.map(u => u.data());
            const occupied = units.filter(u => u.isOccupied).length;
            const total = units.length;

            return {
              id: doc.id,
              property: data.reference || "N/A",
              state: data.displayName || "Unnamed",
              isVacant: total === 0 || occupied < total,
              unitsInfo: total > 0 ? `${occupied}/${total} occupied` : "No units",
            };
          })
        );
        this.properties = props.sort((a, b) => a.property.localeCompare(b.property));
      } catch (e) {
        console.error("Failed to load properties:", e);
      }
    },

    editProperty(item) {
      this.$router.push({ name: "EditProperty", params: { id: item.id } });
    },

    goToAttachments(item) {
      this.$router.push({ name: "Attachments", params: { collection: "properties", id: item.id } });
    },
    goToNotes(item) {
      this.$router.push({ name: "Notes", params: { id: item.id }, query: { collection: "properties" } });
    },
    goToReports(item) {
      this.$router.push({ name: "Reports", params: { id: item.id }, query: { collection: "properties" } });
    },

    confirmDelete(item) {
      this.propertyToDelete = item;
      this.deleteDialog = true;
    },

    async deleteProperty() {
      if (!this.propertyToDelete) return;

      try {
        await deleteDoc(doc(db, "properties", this.propertyToDelete.id));
        this.properties = this.properties.filter(p => p.id !== this.propertyToDelete.id);

        await logEvent(
          "Property Deleted",
          this.propertyToDelete.property,
          "Property Management"
        );

        this.$nextTick(() => {
          alert("Property deleted successfully!");
        });
      } catch (e) {
        console.error("Delete failed:", e);
        alert("Failed to delete property");
      } finally {
        this.deleteDialog = false;
        this.propertyToDelete = null;
      }
    },
  },
};
</script>

<style scoped>
/* Compact Table */
.compact-table :deep(.v-data-table-header th) {
  font-size: 0.8rem !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 8px 10px !important;
  background-color: #f0f0f0 !important;
  color: #000000 !important;
  border-bottom: 2px solid #ccc !important;
}

.compact-table :deep(.v-data-table-header th span) {
  color: #000000 !important;
}

.compact-table :deep(tbody td) {
  font-size: 0.875rem !important;
  padding: 6px 10px !important;
  color: #000000 !important;
}

.compact-table :deep(tbody td .v-chip) {
  font-size: 0.75rem !important;
  height: 22px !important;
}

.compact-table :deep(.v-data-table__wrapper) {
  overflow-x: auto;
}

.compact-table :deep(.v-data-table__expanded__content) {
  font-size: 0.875rem !important;
}

/* Small text field */
.small-text-field :deep(.v-input__slot) {
  min-height: 40px !important;
}

.small-text-field :deep(.v-label) {
  font-size: 0.875rem !important;
  color: #000000 !important;
}

.small-text-field :deep(.v-input__control input) {
  font-size: 0.875rem !important;
  color: #000000 !important;
}

/* Black text */
.black--text {
  color: #000000 !important;
}

/* GOLDEN BUTTONS */
.golden-btn {
  background: linear-gradient(0deg, #f1c284 0%, #e7b87a 100%) !important;
  color: #000000 !important;
  border-radius: 10px !important;
  font-weight: bold !important;
  box-shadow: 0 2px 8px rgba(241, 194, 132, 0.3) !important;
  text-transform: none !important;
  font-size: 0.8rem !important;
  min-height: 34px !important;
}

.golden-btn .v-icon {
  color: #000000 !important;
}

.save-btn {
  background: linear-gradient(0deg, #f1c284 0%, #e7b87a 100%) !important;
  color: #000000 !important;
  box-shadow: 0 2px 8px rgba(241, 194, 132, 0.3) !important;
  font-size: 0.875rem !important;
  min-height: 36px !important;
}

.save-btn .v-icon {
  color: #000000 !important;
}

/* Action buttons */
.action-btn {
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  min-height: 28px !important;
}

.action-btn .v-icon {
  font-size: 18px !important;
}

/* Dialog text */
.v-dialog .v-card-title {
  font-size: 1rem !important;
  color: #000000 !important;
}

.v-dialog .v-card-text {
  font-size: 0.875rem !important;
  color: #000000 !important;
}

.v-dialog .v-btn {
  font-size: 0.8rem !important;
}

/* Responsive — keep mobile readable */
@media (max-width: 768px) {
  .compact-table :deep(.v-data-table-header th) {
    font-size: 0.75rem !important;
    padding: 6px 8px !important;
  }

  .compact-table :deep(tbody td) {
    font-size: 0.8rem !important;
    padding: 5px 8px !important;
  }
}

@media print {
  .v-btn, .v-btn-toggle, .v-select, .v-expansion-panel {
    display: none !important;
  }
}
</style>